import { useState, useEffect } from "react";
import {
  Camera,
  ClipboardList,
  Bot,
  X,
  Plus,
  Trash2,
  User,
  Calendar,
  AlertTriangle,
  Hospital,
  Save,
  Clock,
  FileText,
  Video,
  Upload,
  Eye,
  Search,
} from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";
import { callAnalyze } from "../../../lib/analyzeClient";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { useAuth } from "../../auth/context/AuthContext";
import {
  podeEscreverClinico,
  ehAdministrador,
  ehMedico,
} from "../../../shared/utils/permissoesCategoria";
import { Paciente, AdequacaoAmostral } from "../../../types";
import { useUploadExame, type AchadosExame } from "../hooks/useUploadExame";
import { gerarPromptExameImagem } from "../../ia-analise/services/gerarPromptExameImagem";
import {
  obterContextoGuidelines,
  obterContextoGuidelinesDoador,
} from "../../ia-analise/rag";
// B351 passo 5b (AI_153) — modalidade do doador (fonte única).
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
// Adicionar após os outros imports:
import {
  extrairAchadosIA,
  gerarResumoAchados,
} from "../../ia-analise/utils/extrairAchadosIA";
import { redigirIdentificadoresDoPaciente } from "../../../shared/utils/reidentificacao";
import { redigirPiiEstrutural } from "../../ia-analise/services/anonimizarDados";

interface ListaExamesImagemProps {
  paciente: Paciente;
}

/**
 * 🔍 Detecta o tipo de arquivo pela extensão
 */
function getTipoArquivo(
  nomeArquivo: string
): "imagem" | "pdf" | "video" | "outro" {
  if (!nomeArquivo) return "outro";

  const extensao = nomeArquivo.split(".").pop()?.toLowerCase() || "";

  // Extensões de imagem
  const extensoesImagem = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
  if (extensoesImagem.includes(extensao)) return "imagem";

  // PDF
  if (extensao === "pdf") return "pdf";

  // Vídeos
  const extensoesVideo = ["mp4", "avi", "mov", "wmv", "webm"];
  if (extensoesVideo.includes(extensao)) return "video";

  return "outro";
}

/**
 * 🔍 Retorna o mediaType baseado na extensão do arquivo
 */
function getMediaType(nomeArquivo: string): string {
  const extensao = nomeArquivo.split(".").pop()?.toLowerCase() || "";

  if (extensao === "pdf") return "application/pdf";
  if (extensao === "png") return "image/png";
  if (extensao === "gif") return "image/gif";
  if (extensao === "webp") return "image/webp";
  if (["jpg", "jpeg"].includes(extensao)) return "image/jpeg";

  return "application/octet-stream";
}

export default function ListaExamesImagem({
  paciente,
}: ListaExamesImagemProps) {
  const {
    uploadExame,
    buscarExames,
    deletarExame,
    salvarAchadosIA,
    uploadProgress,
  } = useUploadExame();

  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): nome legado - hoje significa "somente leitura".
  // Cobre convidado E observador (espelho de canWriteClinical das rules).
  const isConvidado = !podeEscreverClinico(usuarioLogado);
  // 🔐 B323 (AI_145): admin mantém deletar após 24h (escape B296). Fonte única
  // da janela de exclusão de imagem — usada na LISTA e no MODAL (o botão do
  // modal antes não checava as 24h; era o buraco reportado por Leonardo).
  const ehAdmin = ehAdministrador(usuarioLogado);
  // §6.3.2/B88 (AI_145): GERAÇÃO/re-análise de IA da imagem é ato médico; o
  // "Ver Análise Anterior" (leitura) permanece à equipe sob !isConvidado.
  const podeGerarIA = ehMedico(usuarioLogado);
  const podeExcluirImagem = (exame: any): boolean => {
    if (isConvidado) return false;
    if (ehAdmin) return true;
    const criadoEmMs = exame?.criadoEm
      ? new Date(exame.criadoEm).getTime()
      : 0;
    if (!criadoEmMs) return false; // sem criadoEm = antigo/travado (admin acima)
    return (Date.now() - criadoEmMs) / (1000 * 60 * 60) < 24;
  };
  const [exames, setExames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarUpload, setMostrarUpload] = useState(false);
  const [termoBusca, setTermoBusca] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState<string>("todos");

  // Formulário de upload
  const [formData, setFormData] = useState({
    tipo: "",
    descricao: "",
    dataExame: "",
    indicacaoClinica: "",
  });
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(
    null
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Modal de visualização
  const [modalAberto, setModalAberto] = useState(false);
  const [exameVisualizando, setExameVisualizando] = useState<any>(null);
  const [indiceAtual, setIndiceAtual] = useState(0);

  // Modal expandido
  const [exameExpandido, setExameExpandido] = useState<any>(null);
  const [erroImagem, setErroImagem] = useState(false);
  const [indiceExameAtual, setIndiceExameAtual] = useState(0);

  // ========================================
  // 🤖 ESTADOS PARA ANÁLISE IA
  // ========================================
  const [mostrarAnaliseIA, setMostrarAnaliseIA] = useState(false);
  const [loadingIA, setLoadingIA] = useState(false);
  const [resultadoIA, setResultadoIA] = useState<string | null>(null);
  const [erroIA, setErroIA] = useState<string | null>(null);

  // Carregar exames
  useEffect(() => {
    carregarExames();
  }, [paciente.id]);

  // 🔄 Reseta erro de imagem quando trocar de exame
  useEffect(() => {
    setErroImagem(false);
  }, [exameExpandido]);

  const carregarExames = async () => {
    setLoading(true);
    const examesCarregados = await buscarExames(paciente.id);

    // 📊 Ordenar por data (mais recente primeiro)
    const examesOrdenados = examesCarregados.sort((a, b) => {
      const getTimestamp = (exame: any) => {
        if (exame.dataExame) {
          if (exame.dataExame.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return new Date(exame.dataExame + "T12:00:00").getTime();
          }
          if (exame.dataExame.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            const [dia, mes, ano] = exame.dataExame.split("/");
            return new Date(`${ano}-${mes}-${dia}T12:00:00`).getTime();
          }
        }
        if (exame.criadoEm) {
          return new Date(exame.criadoEm).getTime();
        }
        if (exame.uploadEm) {
          return new Date(exame.uploadEm).getTime();
        }
        return 0;
      };

      return getTimestamp(b) - getTimestamp(a);
    });

    setExames(examesOrdenados);
    setLoading(false);
  };

  // Fazer upload
  const handleUpload = async () => {
    if (!formData.tipo || !formData.dataExame) {
      alert("Preencha tipo e data do exame!");
      return;
    }

    if (!arquivoSelecionado && !formData.descricao?.trim()) {
      alert("Anexe um arquivo OU preencha a descrição/laudo do exame!");
      return;
    }

    // 🛡️ VALIDAÇÃO DE DATA
    const dataExame = new Date(formData.dataExame + "T00:00:00");

    if (isNaN(dataExame.getTime())) {
      alert("❌ Data inválida! Verifique o formato.");
      return;
    }

    if (ehDataFutura(formData.dataExame)) {
      alert("❌ " + msgDataFutura("Data do exame"));
      return;
    }

    if (dataExame.getFullYear() < 1900) {
      alert("❌ Data muito antiga! Verifique o ano digitado.");
      return;
    }

    const ano = formData.dataExame.split("-")[0];
    if (ano.length !== 4) {
      alert("❌ Ano inválido! O ano deve ter 4 dígitos.");
      return;
    }

    if (arquivoSelecionado) {
      // Upload com arquivo
      const resultado = await uploadExame(
        arquivoSelecionado,
        paciente.id,
        paciente.nome,
        formData
      );

      if (resultado) {
        alert("✅ Exame enviado com sucesso!");
        setMostrarUpload(false);
        setFormData({
          tipo: "",
          descricao: "",
          dataExame: "",
          indicacaoClinica: "",
        });
        setArquivoSelecionado(null);
        setPreviewUrl(null);
        carregarExames();
      }
    } else {
      // Salvar apenas com texto (sem arquivo)
      try {
        const { addDoc, collection, serverTimestamp } = await import(
          "firebase/firestore"
        );
        const { db } = await import("../../../lib/firebase");

        await addDoc(collection(db, "exames-imagem"), {
          pacienteId: paciente.id,
          pacienteNome: paciente.nome,
          tipo: formData.tipo,
          tipoExame: formData.tipo,
          descricao: formData.descricao,
          indicacaoClinica: formData.indicacaoClinica || "",
          dataExame: formData.dataExame,
          apenasTexto: true,
          registradoPor: usuarioLogado?.id || "",
          registradoPorNome: usuarioLogado?.nome || "",
          criadoEm: serverTimestamp(),
        });

        alert("✅ Exame registrado com sucesso (apenas laudo textual)!");
        setMostrarUpload(false);
        setFormData({
          tipo: "",
          descricao: "",
          dataExame: "",
          indicacaoClinica: "",
        });
        carregarExames();
      } catch (error) {
        console.error("❌ Erro ao salvar exame textual:", error);
        alert("❌ Erro ao salvar exame.");
      }
    }
  };

  // Deletar exame
  const handleDeletar = async (exame: any) => {
    const nomeExibicao = exame.nomeArquivo || exame.tipo || "este exame";
    if (!window.confirm(`Tem certeza que deseja deletar ${nomeExibicao}?`)) {
      return;
    }

    // Exame apenas textual (sem arquivo no Storage)
    if (exame.apenasTexto || !exame.caminhoStorage) {
      try {
        const { deleteDoc, doc } = await import("firebase/firestore");
        const { db } = await import("../../../lib/firebase");
        await deleteDoc(doc(db, "exames-imagem", exame.id));

        // B213 — lápide na trilha de auditoria (delete fora do context)
        if (usuarioLogado) {
          const { registrarExclusaoTrilha } = await import(
            "../../../shared/utils/trilhaAuditoria"
          );
          await registrarExclusaoTrilha({
            colecao: "exames-imagem",
            docId: exame.id,
            pacienteId: exame.pacienteId || "",
            rotulo: `${exame.tipoExame || exame.tipo || "Imagem"}${
              exame.dataExame
                ? ` de ${new Date(exame.dataExame).toLocaleDateString("pt-BR")}`
                : ""
            }`,
            usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
          });
        }

        alert("✅ Exame deletado!");
        carregarExames();
      } catch (error) {
        console.error("❌ Erro ao deletar exame textual:", error);
        alert("❌ Erro ao deletar exame");
      }
      return;
    }

    const sucesso = await deletarExame(exame.id, exame.caminhoStorage);
    if (sucesso) {
      alert("✅ Exame deletado!");
      carregarExames();
    } else {
      alert("❌ Erro ao deletar exame");
    }
  };

  // Formatar tamanho
  const formatarTamanho = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // ⬅️ Navegar para exame anterior
  const navegarParaAnterior = () => {
    if (indiceExameAtual > 0) {
      const novoIndice = indiceExameAtual - 1;
      setIndiceExameAtual(novoIndice);
      setExameExpandido(examesFiltrados[novoIndice]);
    }
  };

  // ➡️ Navegar para próximo exame
  const navegarParaProximo = () => {
    if (indiceExameAtual < examesFiltrados.length - 1) {
      const novoIndice = indiceExameAtual + 1;
      setIndiceExameAtual(novoIndice);
      setExameExpandido(examesFiltrados[novoIndice]);
    }
  };

  // ========================================
  // 🤖 FUNÇÃO DE ANÁLISE IA DO EXAME (ATUALIZADA)
  // ========================================
  const handleAnaliseIA = async () => {
    if (!exameExpandido) return;

    const tipoArq = getTipoArquivo(exameExpandido.nomeArquivo || "");
    if (tipoArq !== "pdf" && tipoArq !== "imagem") {
      alert("❌ Análise IA disponível apenas para PDFs e imagens de laudos.");
      return;
    }

    setLoadingIA(true);
    setErroIA(null);
    setResultadoIA(null);
    setMostrarAnaliseIA(true);

    try {
      // 1️⃣ Gerar prompt especializado
      // Passa TODOS os exames (exceto o atual) para o prompt
      // O prompt vai separar por tipo internamente
      const examesAnteriores = exames.filter((e) => e.id !== exameExpandido.id);
      const promptBase = gerarPromptExameImagem(
        paciente,
        exameExpandido,
        examesAnteriores
      );

      // 2️⃣ Buscar contexto dos guidelines (RAG)
      let contextoGuidelines = "";
      try {
        // B351 passo 5b (AI_153) — a imagem do DOADOR usa o mesmo caminho de RAG
        // da análise geral dele. Sem isto, o template de doador (validado ao vivo
        // na AI_152) vinha acompanhado do corpus de cirrose E da query de
        // situação especial — contradizendo por baixo a proibição de vocabulário
        // do receptor que o próprio template impõe.
        if (ehDoadorFigado(paciente.tipoAtendimento)) {
          contextoGuidelines = (await obterContextoGuidelinesDoador()).texto;
        } else {
          const dadosParaRAG = {
            tipoAtendimento: paciente.tipoAtendimento,
            diagnosticoBase: paciente.inscricaoTx?.diagnosticoBase,
          };
          contextoGuidelines = await obterContextoGuidelines(dadosParaRAG as any);
        }
        if (contextoGuidelines) {
          console.log("✅ Contexto de guidelines obtido para exame de imagem!");
        }
      } catch (e) {
        console.warn("⚠️ RAG indisponível para exame de imagem");
      }

      const prompt = promptBase + contextoGuidelines;

      // 3️⃣ Determinar o tipo do arquivo
      const fileType = getMediaType(exameExpandido.nomeArquivo || "");

      // 4️⃣ Chamar API em PARALELO:
      //    (a) Sonnet — análise estruturada (fluxo atual)
      //    (b) Haiku vision — transcrição literal da conclusão do laudista (Bloco 1 AI_34)
      //    Promise.all garante que latência total = max(Sonnet, Haiku) ≈ Sonnet (~30-60s)
      //    Se Haiku falhar, status === "erro_extracao" e fluxo segue normalmente.
      console.log(
        "🤖 Enviando para análise IA (Sonnet + Haiku vision em paralelo)..."
      );

      // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
      // Promise.all preservado: análise principal + transcrição literal Bloco 1 AI_34
      // Tratamento de erro heterogêneo:
      //   - apiResponse (chamada 1): hard-fail (throw) — sem ela não há análise
      //   - transcricaoResponse (chamada 2): soft-fail — null em qualquer erro,
      //     pipeline segue sem transcrição literal
      type AnalyzeResponse = {
        content?: Array<{ type: string; text: string }>;
      };
      type TranscricaoResponse = {
        texto?: string;
        tipoLaudista?: string;
        status?: "sucesso" | "sem_secao_conclusao" | "erro_extracao";
      };

      const [apiResponse, transcricaoResponse] = await Promise.all([
        callAnalyze<AnalyzeResponse>({
          prompt: prompt,
          fileUrl: exameExpandido.url,
          fileType: fileType,
          useThinking: true,
        }),
        callAnalyze<TranscricaoResponse>({
          etapa: "transcrever-conclusao",
          fileUrl: exameExpandido.url,
          fileType: fileType,
        }).catch((err) => {
          console.warn(
            "⚠️ [Bloco 1 AI_34] transcrever-conclusao falhou (fluxo segue):",
            err
          );
          return null;
        }),
      ]);

      // apiResponse é o JSON parseado direto (callAnalyze já trouxe parsed).
      // Se a chamada 1 tivesse falhado, o callAnalyze teria feito throw e
      // não chegaria aqui — Promise.all propaga erros de qualquer item não-catchado.
      // 🔒 B365 (AI_156) — o PDF do laudo traz o CABEÇALHO do serviço (nome do
      // paciente, prontuário) e a IA transcrevia isso para o texto da análise,
      // que é salvo no exame e depois entra no prompt da análise geral (achado
      // ao vivo, 28/10, colangio-RM). Defesa em profundidade: o prompt pede para
      // não copiar o cabeçalho (gerarPromptExameImagem) e aqui redigimos antes
      // de exibir/salvar — prompt é pedido, isto é garantia.
      const conteudo = redigirPiiEstrutural(
        redigirIdentificadoresDoPaciente(
          apiResponse.content?.[0]?.text || "Sem resposta da IA",
          paciente as unknown as Record<string, unknown>
        )
      );
      setResultadoIA(conteudo);
      console.log("✅ Análise Sonnet concluída!");

      // ✅ Bloco 1 AI_34: ler resultado da transcrição literal
      let conclusaoLaudistaLiteral: string | undefined;
      let tipoLaudista:
        | "patologista"
        | "radiologista"
        | "endoscopista"
        | "ecografista"
        | "cardiologista"
        | "outro"
        | undefined;
      let conclusaoLiteralStatus:
        | "sucesso"
        | "sem_secao_conclusao"
        | "erro_extracao"
        | undefined;
      // B120 (AI_66) — transcricaoResponse agora é objeto parseado (ou null)
      // em vez de Response. callAnalyze já fez o parse internamente.
      if (transcricaoResponse) {
        // ✅ AI_43 Fix C — normalizar undefined → "" para compatibilidade Firestore
        conclusaoLaudistaLiteral = transcricaoResponse.texto || "";
        tipoLaudista = (transcricaoResponse.tipoLaudista || "outro") as
          | "patologista"
          | "radiologista"
          | "endoscopista"
          | "ecografista"
          | "cardiologista"
          | "outro";
        conclusaoLiteralStatus = transcricaoResponse.status || "erro_extracao";
        console.log(
          `📋 [Bloco 1 AI_34] Transcrição: ${conclusaoLiteralStatus} (${tipoLaudista}) — ${
            (conclusaoLaudistaLiteral || "").length
          } chars`
        );
      } else {
        conclusaoLiteralStatus = "erro_extracao";
      }

      // ========================================
      // 🆕 5️⃣ EXTRAIR E SALVAR ACHADOS
      // ========================================
      const achados = extrairAchadosIA(conteudo);

      // ✅ AI_40 Patch 2 Alt 4.2 — adequação amostral (só biópsias)
      // Check inline pra evitar cross-import com api/_lib/ (módulo backend-only).
      // Mesmo padrão de normalização usado em api/_lib/adequacaoAmostral.js.
      // Firestore persiste o campo como "tipo" (não "tipoExame") — dívida AI_40 registrada
      const tipoRaw =
        (exameExpandido as any).tipo ?? exameExpandido.tipoExame ?? "";
      const tipoNormalizado = String(tipoRaw)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
      const ehBiopsia =
        tipoNormalizado === "biopsia" || tipoNormalizado.includes("biopsia");

      // ✅ AI_43 B13 — Guard anti-IHQ isolado
      // IHQ é estudo complementar sobre cortes já existentes; nunca traz dados
      // de adequação amostral (comprimento, EP, fragmentos). Disparar a action
      // em IHQ isolado retorna nao_avaliavel poluindo Firestore.
      // 2 camadas de exclusão (qualquer uma dispara → skip):
      //   (a) campo TIPO do ACHADOS_EXAME começa com "Imuno-histoquímico"/"IHQ"
      //       SEM combinação com anatomopatológico/biópsia
      //   (b) conteúdo não contém nenhum marcador de amostra real
      const tipoDoAchados =
        conteudo
          .match(/ACHADOS_EXAME[\s\S]*?TIPO:\s*([^\n]+)/i)?.[1]
          ?.trim()
          .toLowerCase() ?? "";
      // ✅ AI_43 B13 v2 — Camada 1: primeiro token do TIPO é autoritativo.
      // Se começa com "imuno"/"ihq", é laudo IHQ (reação sobre bloco prévio),
      // mesmo quando o TIPO menciona "biópsia" a seguir (é material-origem).
      const ehIHQIsolado = /^(imuno|ihq)/.test(tipoDoAchados);

      // ✅ AI_43 B13 v2 — Camada 2: exige QUANTIFICAÇÃO numérica de amostra.
      // Menções soltas ("agulha", "fragmento" sem número) não provam que há
      // dados amostrais extraíveis. Sem número + palavra amostral → skip.
      const temMarcadorAmostral =
        /\d+\s*(fragmento|espa[cç]o|cilindro|EP\b)|\d+[,.]?\d*\s*cm\s*de\s*cilindro|\d+\s*mm\s*de\s*comprimento|espa[cç]os?\s*[-–]?\s*porta\s*[:=]?\s*\d+/i.test(
          conteudo
        );

      let adequacaoAmostral: AdequacaoAmostral | undefined;
      if (
        ehBiopsia &&
        conteudo &&
        conteudo.trim().length > 0 &&
        !ehIHQIsolado &&
        temMarcadorAmostral
      ) {
        console.log(
          `🔬 [AI_43 B13] Guard OK — tipoAchados="${tipoDoAchados.slice(
            0,
            60
          )}" | ehIHQIsolado=${ehIHQIsolado} | temMarcadorAmostral=${temMarcadorAmostral}`
        );
        try {
          // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
          // Soft-fail preservado: erro silencia em console.warn, fluxo segue
          adequacaoAmostral = await callAnalyze<AdequacaoAmostral>({
            etapa: "avaliar-adequacao-amostral",
            analiseCompleta: conteudo,
          });
          console.log(
            `📏 [AI_40 Patch 2] Adequação=${adequacaoAmostral.classificacao} | ${adequacaoAmostral.comprimentoMm}mm | ${adequacaoAmostral.espacosPorta}EP | fonte=${adequacaoAmostral.fonteExtracao}`
          );
        } catch (err) {
          console.warn(
            "⚠️ [AI_40 Patch 2] avaliar-adequacao-amostral falhou (fluxo segue):",
            err
          );
        }
      } else if (ehBiopsia) {
        // ✅ AI_43 B13 — skip observável: biópsia no tipo mas sem dados amostrais
        // (IHQ isolado, laudo pobre, ou anatomopat que não quantificou)
        console.log(
          `🔬 [AI_43 B13] Skip adequação — tipoAchados="${tipoDoAchados.slice(
            0,
            60
          )}" | ehIHQIsolado=${ehIHQIsolado} | temMarcadorAmostral=${temMarcadorAmostral}`
        );
      }
      if (achados) {
        // ✅ Bloco 1 AI_34 + AI_43 Fix C — enriquecer com transcrição literal,
        // aceitando strings vazias mas nunca undefined (Firestore rejeita undefined)
        achados.conclusaoLaudistaLiteral = conclusaoLaudistaLiteral ?? "";
        achados.tipoLaudista = tipoLaudista ?? "outro";
        achados.conclusaoLiteralStatus =
          conclusaoLiteralStatus ?? "erro_extracao";
        achados.conclusaoLiteralGeradaEm = new Date().toISOString();

        // ✅ AI_40 Patch 2 Alt 4.2: enriquecer achados com adequação amostral
        if (adequacaoAmostral) {
          achados.adequacaoAmostral = adequacaoAmostral;
        }

        const resumo = gerarResumoAchados(achados);
        const salvou = await salvarAchadosIA(
          exameExpandido.id,
          achados,
          resumo,
          conteudo
        );

        if (salvou) {
          console.log("✅ Achados salvos no Firebase!");
          // Atualizar o exame local com os achados E análise completa
          setExameExpandido({
            ...exameExpandido,
            achados: achados,
            resumoAchados: resumo,
            analiseCompleta: conteudo,
          });
          // Recarregar lista de exames para mostrar o resumo
          carregarExames();
        }
      } else {
        console.warn(
          "⚠️ Não foi possível extrair achados estruturados — salvando análise como texto"
        );

        // ✅ AI_42 Camada B — fallback preserva Bloco 1 + adequação amostral
        // Mesmo sem ACHADOS_EXAME emitido pelo Sonnet, Bloco 1 (AI_34) e
        // adequacaoAmostral (AI_40 Patch 2) são dados deterministicamente
        // obtidos por outras actions e não devem ser perdidos.
        const temBloco1 =
          !!conclusaoLaudistaLiteral ||
          !!tipoLaudista ||
          !!conclusaoLiteralStatus;
        const temAdequacao = !!adequacaoAmostral;
        const achadosMinimo: Partial<AchadosExame> | null =
          temBloco1 || temAdequacao
            ? {
                ...(conclusaoLaudistaLiteral !== undefined && {
                  conclusaoLaudistaLiteral,
                }),
                ...(tipoLaudista !== undefined && { tipoLaudista }),
                ...(conclusaoLiteralStatus !== undefined && {
                  conclusaoLiteralStatus,
                }),
                ...(temBloco1 && {
                  conclusaoLiteralGeradaEm: new Date().toISOString(),
                }),
                ...(temAdequacao && { adequacaoAmostral }),
              }
            : null;

        if (achadosMinimo) {
          console.log(
            `🧩 [AI_42 Camada B] Shell mínimo preservado: Bloco1=${temBloco1} Adequacao=${temAdequacao}`
          );
        }

        const salvou = await salvarAchadosIA(
          exameExpandido.id,
          achadosMinimo,
          "Análise realizada (ver detalhes)",
          conteudo
        );
        if (salvou) {
          console.log("✅ Análise salva como texto!");
          setExameExpandido({
            ...exameExpandido,
            achados: achadosMinimo || undefined,
            analiseCompleta: conteudo,
            resumoAchados: "Análise realizada (ver detalhes)",
          });
          carregarExames();
        }
      }
    } catch (error: any) {
      console.error("Erro na análise IA:", error);
      setErroIA(error.message || "Erro ao processar análise");
    } finally {
      setLoadingIA(false);
    }
  };

  // Fechar modal de análise IA
  const fecharAnaliseIA = () => {
    setMostrarAnaliseIA(false);
    setResultadoIA(null);
    setErroIA(null);
  };

  // Abrir modal
  const abrirModal = (exame: any, indice: number) => {
    setExameVisualizando(exame);
    setIndiceAtual(indice);
    setModalAberto(true);
  };

  // Fechar modal
  const fecharModal = () => {
    setModalAberto(false);
    setExameVisualizando(null);
  };

  // Navegar para anterior
  const exameAnterior = () => {
    if (indiceAtual > 0) {
      const novoIndice = indiceAtual - 1;
      setIndiceAtual(novoIndice);
      setExameVisualizando(exames[novoIndice]);
    }
  };

  // Navegar para próximo
  const exameProximo = () => {
    if (indiceAtual < exames.length - 1) {
      const novoIndice = indiceAtual + 1;
      setIndiceAtual(novoIndice);
      setExameVisualizando(exames[novoIndice]);
    }
  };

  // 🔍 Filtrar exames por busca e tipo
  const examesFiltrados = exames.filter((exame) => {
    if (tipoFiltro !== "todos" && exame.tipo !== tipoFiltro) {
      return false;
    }

    if (termoBusca) {
      const busca = termoBusca.toLowerCase();
      return (
        exame.tipo?.toLowerCase().includes(busca) ||
        exame.descricao?.toLowerCase().includes(busca) ||
        exame.nomeArquivo?.toLowerCase().includes(busca)
      );
    }

    return true;
  });

  // 📊 Contar tipos únicos de exames
  const tiposDisponiveis = Array.from(
    new Set(exames.map((e) => e.tipo))
  ).sort();

  // ⌨️ Atalhos de teclado para navegação
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!exameExpandido) return;

      if (e.key === "ArrowLeft") {
        navegarParaAnterior();
      } else if (e.key === "ArrowRight") {
        navegarParaProximo();
      } else if (e.key === "Escape") {
        setExameExpandido(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [exameExpandido, indiceExameAtual, examesFiltrados]);

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "24px",
      }}
    >
      {/* CABEÇALHO E BUSCA */}
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: theme.gray[800],
              fontFamily: theme.fonts.sans,
              margin: 0,
            }}
          >
            <Camera size={18} style={{ marginRight: "8px" }} /> Exames de Imagem
          </h3>

          {!isConvidado && (
            <button
              onClick={() => setMostrarUpload(!mostrarUpload)}
              style={{
                padding: "12px 24px",
                background: mostrarUpload
                  ? theme.gray[300]
                  : `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: mostrarUpload ? theme.gray[700] : "white",
                border: "none",
                borderRadius: theme.borderRadius.lg,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              {mostrarUpload ? (
                <>
                  <X size={14} style={{ marginRight: "6px" }} /> Cancelar
                </>
              ) : (
                <>
                  <Plus size={14} style={{ marginRight: "6px" }} /> Novo Exame
                </>
              )}
            </button>
          )}
        </div>

        {/* 🔍 CAMPO DE BUSCA */}
        {!mostrarUpload && exames.length > 0 && (
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Buscar por tipo, descrição ou arquivo..."
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              style={{
                width: "calc(100% - 4px)",
                padding: "12px 16px 12px 40px",
                border: `2px solid ${
                  termoBusca ? theme.primary[300] : theme.gray[300]
                }`,
                borderRadius: theme.borderRadius.lg,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "18px",
              }}
            >
              <Search size={16} />
            </span>
            {termoBusca && (
              <button
                onClick={() => setTermoBusca("")}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  color: theme.gray[400],
                }}
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* 📁 FILTROS POR TIPO */}
        {!mostrarUpload && exames.length > 0 && tiposDisponiveis.length > 1 && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "12px",
            }}
          >
            <button
              onClick={() => setTipoFiltro("todos")}
              style={{
                padding: "8px 16px",
                background:
                  tipoFiltro === "todos"
                    ? `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`
                    : theme.gray[100],
                color: tipoFiltro === "todos" ? "white" : theme.gray[700],
                border:
                  tipoFiltro === "todos"
                    ? `2px solid ${theme.primary[600]}`
                    : `2px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.lg,
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                transition: "all 0.2s",
              }}
            >
              <ClipboardList size={14} style={{ marginRight: "6px" }} /> Todos (
              {exames.length})
            </button>

            {tiposDisponiveis.map((tipo) => {
              const count = exames.filter((e) => e.tipo === tipo).length;
              return (
                <button
                  key={tipo}
                  onClick={() => setTipoFiltro(tipo)}
                  style={{
                    padding: "8px 16px",
                    background:
                      tipoFiltro === tipo
                        ? `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`
                        : theme.gray[100],
                    color: tipoFiltro === tipo ? "white" : theme.gray[700],
                    border:
                      tipoFiltro === tipo
                        ? `2px solid ${theme.primary[600]}`
                        : `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                    transition: "all 0.2s",
                  }}
                >
                  {tipo} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* FORMULÁRIO DE UPLOAD */}
      {mostrarUpload && (
        <div
          style={{
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Upload size={16} style={{ marginRight: "8px" }} /> Upload de Exame
          </h4>

          <div style={{ display: "grid", gap: "16px" }}>
            {/* TIPO */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Tipo de Exame <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                value={formData.tipo}
                onChange={(e) =>
                  setFormData({ ...formData, tipo: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <option value="">Selecione...</option>
                <option value="TC Abdome">TC Abdome</option>
                <option value="TC Tórax">TC Tórax</option>
                <option value="TC Abdome e Tórax">TC Abdome e Tórax</option>
                <option value="TC Crânio">TC Crânio</option>
                <option value="TC Outros">TC Outros</option>
                <option value="RM Abdome">RM Abdome</option>
                <option value="RM Tórax">RM Tórax</option>
                <option value="RM Crânio">RM Crânio</option>
                <option value="RM Outros">RM Outros</option>
                <option value="Ultrassom">Ultrassom</option>
                <option value="Raio-X">Raio-X</option>
                <option value="Endoscopia">Endoscopia</option>
                <option value="Colonoscopia">Colonoscopia</option>
                <option value="Ecocardiograma">Ecocardiograma</option>
                <option value="Doppler">Doppler</option>
                <option value="PET-CT">PET-CT</option>
                <option value="Cintilografia">Cintilografia</option>
                <option value="Mapeamento Ósseo">Mapeamento Ósseo</option>
                <option value="Elastografia">Elastografia</option>
                <option value="Espirometria">Espirometria</option>
                <option value="Cateterismo">Cateterismo Cardíaco</option>
                <option value="Prova Função Pulmonar">
                  Prova Função Pulmonar
                </option>
                <option value="Biópsia">Biópsia</option>
                <option value="Quimioembolização">
                  Quimioembolização (TACE)
                </option>
                <option value="Ablação">Ablação (RFA/MWA)</option>
                <option value="CPRE">
                  CPRE — Colangiografia Retrógrada Endoscópica
                </option>
                <option value="Colangioressonância">Colangioressonância</option>
                <option value="Colangiografia Transparietohepática">
                  Colangiografia Transparietohepática
                </option>
                <option value="Ultrassom Abdome Superior">
                  Ultrassom Abdome Superior
                </option>
                <option value="Ultrassom Abdome Superior com Doppler">
                  Ultrassom Abdome Superior com Doppler Hepático
                </option>
                <option value="Ecoendoscopia">Ecoendoscopia</option>
                <option value="Outros">Outros</option>
              </select>
            </div>

            {/* DATA */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Data do Exame <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="date"
                value={formData.dataExame}
                max={hojeISO()}
                min="1900-01-01"
                onChange={(e) =>
                  setFormData({ ...formData, dataExame: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>

            {/* DESCRIÇÃO */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Descrição
              </label>
              <textarea
                value={formData.descricao}
                onChange={(e) =>
                  setFormData({ ...formData, descricao: e.target.value })
                }
                placeholder="Ex: Tomografia de abdome com contraste"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                  minHeight: "80px",
                  resize: "vertical",
                }}
              />
            </div>

            {/* INDICAÇÃO CLÍNICA */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Indicação Clínica{" "}
                <span style={{ fontSize: "12px", color: theme.gray[500] }}>
                  (opcional — motivo pelo qual o exame foi solicitado)
                </span>
              </label>
              <textarea
                value={formData.indicacaoClinica}
                onChange={(e) =>
                  setFormData({ ...formData, indicacaoClinica: e.target.value })
                }
                placeholder="Ex: Tosse produtiva há 10 dias em paciente pós-Tx | Dor abdominal em QSD | Vigilância CHC de rotina | Estadiamento pré-transplante"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                  minHeight: "60px",
                  resize: "vertical",
                }}
              />
            </div>

            {/* ARQUIVO */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Arquivo{" "}
                <span style={{ fontSize: "12px", color: theme.gray[500] }}>
                  (opcional se preencher descrição)
                </span>
              </label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.dcm,.mp4,.mov,.avi,.mkv"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    setArquivoSelecionado(file);

                    if (file.type.startsWith("image/")) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPreviewUrl(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    } else {
                      setPreviewUrl(null);
                    }
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: theme.gray[500],
                  marginTop: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Formatos: JPG, PNG, PDF, DICOM, MP4, MOV, AVI, MKV • Máximo:
                500MB
              </p>
            </div>

            {/* PREVIEW E INFO DO ARQUIVO */}
            {arquivoSelecionado && (
              <div
                style={{
                  padding: "16px",
                  background: theme.gray[100],
                  borderRadius: theme.borderRadius.lg,
                  border: `2px solid ${theme.primary[200]}`,
                }}
              >
                <h5
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: theme.gray[800],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  📎 Arquivo Selecionado
                </h5>

                <div
                  style={{ display: "flex", gap: "16px", alignItems: "center" }}
                >
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: theme.borderRadius.md,
                        border: `2px solid ${theme.gray[300]}`,
                      }}
                    />
                  )}

                  {!previewUrl && (
                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: theme.gray[200],
                        borderRadius: theme.borderRadius.md,
                        fontSize: "48px",
                      }}
                    >
                      {arquivoSelecionado.type.includes("pdf") && (
                        <FileText size={14} />
                      )}
                      {arquivoSelecionado.type.includes("video") && (
                        <Video size={14} />
                      )}
                      {arquivoSelecionado.type.includes("dicom") && (
                        <Hospital size={14} style={{ marginRight: "4px" }} />
                      )}
                      {!arquivoSelecionado.type.includes("pdf") &&
                        !arquivoSelecionado.type.includes("video") &&
                        !arquivoSelecionado.type.includes("dicom") && (
                          <ClipboardList size={14} />
                        )}
                    </div>
                  )}

                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme.gray[800],
                        marginBottom: "8px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {arquivoSelecionado.name}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        fontSize: "13px",
                        color: theme.gray[600],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <span>
                        📦 Tamanho: {formatarTamanho(arquivoSelecionado.size)}
                      </span>
                      <span>
                        <ClipboardList
                          size={12}
                          style={{ marginRight: "4px" }}
                        />{" "}
                        Tipo: {arquivoSelecionado.type || "Desconhecido"}
                      </span>
                    </div>

                    {arquivoSelecionado.size > 500 * 1024 * 1024 && (
                      <p
                        style={{
                          marginTop: "8px",
                          color: "#dc2626",
                          fontSize: "13px",
                          fontWeight: "600",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <AlertTriangle
                          size={14}
                          style={{ marginRight: "6px" }}
                        />{" "}
                        Arquivo maior que 500MB! O upload pode falhar.
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setArquivoSelecionado(null);
                      setPreviewUrl(null);
                    }}
                    style={{
                      padding: "8px 12px",
                      background: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: theme.borderRadius.md,
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* BARRA DE PROGRESSO */}
            {uploadProgress.uploading && (
              <div>
                <div
                  style={{
                    width: "100%",
                    height: "8px",
                    background: theme.gray[200],
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${uploadProgress.progress}%`,
                      height: "100%",
                      background: `linear-gradient(90deg, ${theme.primary[600]} 0%, ${theme.primary[500]} 100%)`,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: theme.gray[600],
                    marginTop: "4px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Enviando... {uploadProgress.progress}%
                </p>
              </div>
            )}

            {/* BOTÃO ENVIAR */}
            <button
              onClick={handleUpload}
              disabled={uploadProgress.uploading}
              style={{
                padding: "12px 24px",
                background: uploadProgress.uploading
                  ? theme.gray[300]
                  : `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.lg,
                fontSize: "14px",
                fontWeight: "600",
                cursor: uploadProgress.uploading ? "not-allowed" : "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              {uploadProgress.uploading ? (
                <>
                  <Clock size={14} style={{ marginRight: "6px" }} /> Enviando...
                </>
              ) : (
                <>
                  <Upload size={14} style={{ marginRight: "6px" }} /> Enviar
                  Exame
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* LISTA DE EXAMES */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: theme.gray[500], fontFamily: theme.fonts.sans }}>
            Carregando exames...
          </p>
        </div>
      ) : examesFiltrados.length === 0 ? (
        termoBusca ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.lg,
            }}
          >
            <p
              style={{
                fontSize: "16px",
                color: theme.gray[500],
                fontFamily: theme.fonts.sans,
                marginBottom: "12px",
              }}
            >
              <Search size={14} style={{ marginRight: "6px" }} /> Nenhum exame
              encontrado para "{termoBusca}"
            </p>
            <button
              onClick={() => setTermoBusca("")}
              style={{
                padding: "8px 16px",
                background: theme.primary[600],
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              Limpar busca
            </button>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.lg,
            }}
          >
            <p
              style={{
                fontSize: "16px",
                color: theme.gray[500],
                fontFamily: theme.fonts.sans,
              }}
            >
              <Camera size={48} color={theme.gray[300]} /> Nenhum exame de
              imagem cadastrado
            </p>
          </div>
        )
      ) : (
        <div style={{ display: "grid", gap: "16px" }}>
          {examesFiltrados.map((exame, index) => (
            <div
              key={exame.id}
              onClick={() => {
                setExameExpandido(exame);
                setIndiceExameAtual(index);
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                border: `1px solid ${theme.gray[200]}`,
                borderRadius: theme.borderRadius.lg,
                transition: "all 0.2s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.gray[50];
                e.currentTarget.style.borderColor = theme.primary[300];
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "white";
                e.currentTarget.style.borderColor = theme.gray[200];
              }}
            >
              <div style={{ flex: 1 }}>
                <h4
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: theme.gray[800],
                    margin: "0 0 8px 0",
                    fontFamily: theme.fonts.sans,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {exame.tipo}
                  {(exame.achados || exame.analiseCompleta) && (
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#059669",
                        background: "#d1fae5",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontWeight: "600",
                      }}
                    >
                      ✓ Analisado
                    </span>
                  )}
                  {/* ✅ Bloco 1 AI_34 — badge de transcrição literal verificada */}
                  {exame.achados?.conclusaoLiteralStatus === "sucesso" &&
                    exame.achados?.conclusaoLaudistaLiteral && (
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#1e40af",
                          background: "#dbeafe",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontWeight: "600",
                        }}
                        title="Transcrição literal do laudista verificada por Haiku vision"
                      >
                        📋 Literal verificado
                      </span>
                    )}
                  {exame.achados &&
                    !exame.achados.conclusaoLiteralStatus &&
                    (exame.achados.conclusao || exame.analiseCompleta) && (
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          background: "#fef3c7",
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontWeight: "600",
                        }}
                        title="Análise anterior ao Bloco 1 AI_34 — sem transcrição literal verificada. Reanalise para popular."
                      >
                        ⚠️ Análise legada
                      </span>
                    )}
                </h4>
                {exame.descricao && (
                  <p
                    style={{
                      fontSize: "14px",
                      color: theme.gray[600],
                      margin: "0 0 8px 0",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {exame.descricao}
                  </p>
                )}
                {/* ✅ NOVO: Mostrar resumo dos achados se existir */}
                {exame.resumoAchados && (
                  <p
                    style={{
                      fontSize: "13px",
                      color: theme.primary[700],
                      margin: "0 0 8px 0",
                      fontFamily: theme.fonts.sans,
                      fontWeight: "500",
                      background: theme.primary[50],
                      padding: "4px 8px",
                      borderRadius: theme.borderRadius.sm,
                      display: "inline-block",
                    }}
                  >
                    <Bot size={12} style={{ marginRight: "4px" }} />{" "}
                    {exame.resumoAchados}
                  </p>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    fontSize: "13px",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                    flexWrap: "wrap",
                  }}
                >
                  <span>
                    <Calendar size={12} style={{ marginRight: "4px" }} />{" "}
                    {(() => {
                      if (exame.dataExame && exame.dataExame.includes("-")) {
                        const [ano, mes, dia] = exame.dataExame.split("-");
                        return `${dia}/${mes}/${ano}`;
                      }
                      return exame.dataExame || "Data não informada";
                    })()}
                  </span>
                  {exame.nomeArquivo && (
                    <span>
                      <FileText size={12} style={{ marginRight: "4px" }} />{" "}
                      {exame.nomeArquivo}
                    </span>
                  )}
                  {exame.tamanho > 0 && (
                    <span>
                      <Save size={12} style={{ marginRight: "4px" }} />{" "}
                      {formatarTamanho(exame.tamanho)}
                    </span>
                  )}
                  {exame.apenasTexto && (
                    <span
                      style={{ color: theme.primary[600], fontWeight: "600" }}
                    >
                      📝 Apenas laudo textual
                    </span>
                  )}
                  <span>
                    <User size={12} style={{ marginRight: "4px" }} />{" "}
                    {exame.uploadPorNome || exame.registradoPorNome || "—"}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {exame.url && (
                  <a
                    href={exame.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    style={{
                      padding: "8px 16px",
                      background: theme.primary[600],
                      color: "white",
                      border: "none",
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontFamily: theme.fonts.sans,
                      textDecoration: "none",
                    }}
                  >
                    <Eye size={14} style={{ marginRight: "6px" }} /> Ver
                  </a>
                )}
                {podeExcluirImagem(exame) && (
                    <button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleDeletar(exame);
                      }}
                      style={{
                        padding: "8px 16px",
                        background: "#dc2626",
                        color: "white",
                        border: "none",
                        borderRadius: theme.borderRadius.md,
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========================================
          📋 MODAL DE DETALHES DO EXAME
          ======================================== */}
      {exameExpandido && (
        <div
          onClick={() => setExameExpandido(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: theme.borderRadius.lg,
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            {/* HEADER DO MODAL */}
            <div
              style={{
                padding: "24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: "white",
                borderTopLeftRadius: theme.borderRadius.lg,
                borderTopRightRadius: theme.borderRadius.lg,
              }}
            >
              {/* SETA ESQUERDA */}
              <button
                onClick={navegarParaAnterior}
                disabled={indiceExameAtual === 0}
                style={{
                  background:
                    indiceExameAtual === 0
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: indiceExameAtual === 0 ? "not-allowed" : "pointer",
                  fontSize: "24px",
                  color: "white",
                  transition: "background 0.2s",
                  opacity: indiceExameAtual === 0 ? 0.5 : 1,
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (indiceExameAtual > 0) {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.3)";
                  }
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.background =
                    indiceExameAtual === 0
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(255, 255, 255, 0.2)";
                }}
              >
                ←
              </button>

              {/* TÍTULO E CONTADOR */}
              <div style={{ textAlign: "center", flex: 1 }}>
                <h3
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: "20px",
                    fontWeight: "700",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <ClipboardList size={16} style={{ marginRight: "8px" }} />{" "}
                  Detalhes do Exame
                </h3>
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.9,
                    fontWeight: "500",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {indiceExameAtual + 1} de {examesFiltrados.length}
                </div>
              </div>

              {/* SETA DIREITA */}
              <button
                onClick={navegarParaProximo}
                disabled={indiceExameAtual === examesFiltrados.length - 1}
                style={{
                  background:
                    indiceExameAtual === examesFiltrados.length - 1
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor:
                    indiceExameAtual === examesFiltrados.length - 1
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "24px",
                  color: "white",
                  transition: "background 0.2s",
                  opacity:
                    indiceExameAtual === examesFiltrados.length - 1 ? 0.5 : 1,
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (indiceExameAtual < examesFiltrados.length - 1) {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.3)";
                  }
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.background =
                    indiceExameAtual === examesFiltrados.length - 1
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(255, 255, 255, 0.2)";
                }}
              >
                →
              </button>

              {/* BOTÃO FECHAR X */}
              <button
                onClick={() => setExameExpandido(null)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "20px",
                  color: "white",
                  transition: "background 0.2s",
                  marginLeft: "12px",
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) =>
                  (e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.3)")
                }
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) =>
                  (e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.2)")
                }
              >
                ×
              </button>
            </div>

            {/* CONTEÚDO DO MODAL */}
            <div style={{ padding: "24px" }}>
              {/* 🖼️ SEÇÃO DE PREVIEW */}
              <div style={{ marginBottom: "24px" }}>
                {(() => {
                  const tipoArquivo = getTipoArquivo(
                    exameExpandido.nomeArquivo || ""
                  );

                  // 📸 CASO 1: ARQUIVO É IMAGEM
                  if (tipoArquivo === "imagem" && !erroImagem) {
                    return (
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "8px",
                            fontFamily: theme.fonts.sans,
                            fontWeight: "600",
                          }}
                        >
                          🖼️ Preview da Imagem
                        </div>
                        <div
                          style={{
                            background: "#f9fafb",
                            borderRadius: theme.borderRadius.lg,
                            padding: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={exameExpandido.url}
                            alt={exameExpandido.descricao || "Exame de imagem"}
                            onError={() => setErroImagem(true)}
                            style={{
                              maxHeight: "400px",
                              maxWidth: "100%",
                              objectFit: "contain",
                              borderRadius: theme.borderRadius.md,
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                        </div>
                      </div>
                    );
                  }

                  // ❌ CASO 2: IMAGEM COM ERRO
                  if (tipoArquivo === "imagem" && erroImagem) {
                    return (
                      <div
                        style={{
                          background: "#fef2f2",
                          border: "2px solid #fecaca",
                          borderRadius: theme.borderRadius.lg,
                          padding: "24px",
                          textAlign: "center",
                          marginBottom: "16px",
                        }}
                      >
                        <AlertTriangle
                          size={64}
                          color="#f59e0b"
                          style={{ marginBottom: "12px" }}
                        />
                        <p
                          style={{
                            fontSize: "16px",
                            color: "#991b1b",
                            fontWeight: "600",
                            margin: "0 0 8px 0",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          Erro ao carregar imagem
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#dc2626",
                            margin: 0,
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          A imagem pode estar corrompida ou indisponível
                        </p>
                      </div>
                    );
                  }

                  // 📄 CASO 3: ARQUIVO É PDF
                  if (tipoArquivo === "pdf") {
                    return (
                      <div
                        style={{
                          background: "#fef2f2",
                          border: "2px solid #fecaca",
                          borderRadius: theme.borderRadius.lg,
                          padding: "32px",
                          textAlign: "center",
                          marginBottom: "16px",
                        }}
                      >
                        <FileText
                          size={80}
                          color={theme.gray[300]}
                          style={{ marginBottom: "16px" }}
                        />
                        <p
                          style={{
                            fontSize: "18px",
                            color: "#111827",
                            fontWeight: "600",
                            margin: "0 0 8px 0",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          Documento PDF
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#6b7280",
                            margin: 0,
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          Clique no botão "Ver Arquivo" abaixo para abrir
                        </p>
                      </div>
                    );
                  }

                  // 🎬 CASO 4: ARQUIVO É VÍDEO
                  if (tipoArquivo === "video") {
                    return (
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "8px",
                            fontFamily: theme.fonts.sans,
                            fontWeight: "600",
                          }}
                        >
                          🎬 Preview do Vídeo
                        </div>
                        <div
                          style={{
                            background: "#000",
                            borderRadius: theme.borderRadius.lg,
                            overflow: "hidden",
                          }}
                        >
                          <video
                            src={exameExpandido.url}
                            controls
                            preload="metadata"
                            style={{
                              width: "100%",
                              maxHeight: "400px",
                            }}
                          >
                            <p
                              style={{
                                color: "white",
                                padding: "16px",
                                textAlign: "center",
                              }}
                            >
                              Seu navegador não suporta reprodução de vídeo
                            </p>
                          </video>
                        </div>
                      </div>
                    );
                  }

                  // 📋 CASO 5: OUTRO TIPO DE ARQUIVO
                  return (
                    <div
                      style={{
                        background: "#f9fafb",
                        border: "2px solid #e5e7eb",
                        borderRadius: theme.borderRadius.lg,
                        padding: "32px",
                        textAlign: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <ClipboardList
                        size={80}
                        color={theme.gray[300]}
                        style={{ marginBottom: "16px" }}
                      />
                      <p
                        style={{
                          fontSize: "18px",
                          color: "#111827",
                          fontWeight: "600",
                          margin: "0 0 8px 0",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Arquivo do tipo:{" "}
                        {exameExpandido.nomeArquivo
                          ?.split(".")
                          .pop()
                          ?.toUpperCase() || "DESCONHECIDO"}
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#6b7280",
                          margin: 0,
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Clique no botão "Ver Arquivo" abaixo para abrir
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* INFO DO EXAME */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "4px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  <Calendar size={14} style={{ marginRight: "6px" }} /> Data do
                  Exame
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    color: "#111827",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {(() => {
                    if (
                      exameExpandido.dataExame &&
                      exameExpandido.dataExame.includes("-")
                    ) {
                      const [ano, mes, dia] =
                        exameExpandido.dataExame.split("-");
                      return `${dia}/${mes}/${ano}`;
                    }
                    return exameExpandido.dataExame || "Data não informada";
                  })()}
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "4px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  <FileText size={14} style={{ marginRight: "6px" }} /> Arquivo
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    color: "#111827",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {exameExpandido.nomeArquivo || exameExpandido.nome}
                </div>
              </div>

              {/* BOTÕES DE AÇÃO */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  paddingTop: "20px",
                  borderTop: "1px solid #e5e7eb",
                  flexWrap: "wrap",
                }}
              >
                {/* 🤖 BOTÃO ANÁLISE IA */}
                {!isConvidado &&
                  (getTipoArquivo(exameExpandido.nomeArquivo || "") === "pdf" ||
                    getTipoArquivo(exameExpandido.nomeArquivo || "") ===
                      "imagem") && (
                    <>
                      {exameExpandido.analiseCompleta && (
                        <button
                          onClick={() => {
                            setResultadoIA(exameExpandido.analiseCompleta);
                            setMostrarAnaliseIA(true);
                            setErroIA(null);
                          }}
                          style={{
                            flex: 1,
                            padding: "12px 24px",
                            background:
                              "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontFamily: theme.fonts.sans,
                            transition: "all 0.2s",
                          }}
                        >
                          <Eye size={14} style={{ marginRight: "6px" }} /> Ver
                          Análise Anterior
                        </button>
                      )}
                      {/* §6.3.2 (AI_145): GERAR/re-analisar é ato médico (o
                          "Ver Análise Anterior" acima é leitura, fica p/ a equipe) */}
                      {podeGerarIA && (
                      <button
                        onClick={handleAnaliseIA}
                        disabled={loadingIA}
                        style={{
                          flex: 1,
                          padding: "12px 24px",
                          background: loadingIA
                            ? "#9ca3af"
                            : "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: theme.borderRadius.md,
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: loadingIA ? "not-allowed" : "pointer",
                          fontFamily: theme.fonts.sans,
                          transition: "all 0.2s",
                        }}
                      >
                        {loadingIA ? (
                          <>
                            <Clock size={14} style={{ marginRight: "6px" }} />{" "}
                            Analisando...
                          </>
                        ) : (
                          <>
                            <Bot size={14} style={{ marginRight: "6px" }} />{" "}
                            {exameExpandido.analiseCompleta
                              ? "Nova Análise IA"
                              : "Análise IA"}
                          </>
                        )}
                      </button>
                      )}
                    </>
                  )}

                {exameExpandido.url && (
                  <a
                    href={exameExpandido.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      padding: "12px 24px",
                      background: theme.primary[600],
                      color: "white",
                      border: "none",
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontFamily: theme.fonts.sans,
                      textDecoration: "none",
                      textAlign: "center",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
                      (e.currentTarget.style.background = theme.primary[700])
                    }
                    onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
                      (e.currentTarget.style.background = theme.primary[600])
                    }
                  >
                    <Eye size={14} style={{ marginRight: "6px" }} /> Ver Arquivo
                  </a>
                )}
                {podeExcluirImagem(exameExpandido) && (
                  <button
                    onClick={() => {
                      handleDeletar(exameExpandido);
                      setExameExpandido(null);
                    }}
                    style={{
                      padding: "12px 24px",
                      background: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontFamily: theme.fonts.sans,
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) =>
                      (e.currentTarget.style.background = "#b91c1c")
                    }
                    onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) =>
                      (e.currentTarget.style.background = "#dc2626")
                    }
                  >
                    <Trash2 size={14} style={{ marginRight: "6px" }} /> Deletar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================
          🤖 MODAL DE ANÁLISE IA
          ======================================== */}
      {mostrarAnaliseIA && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={fecharAnaliseIA}
        >
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do Modal */}
            <div
              style={{
                padding: "20px 24px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                🩻 Análise Inteligente de Exame de Imagem
              </h2>
              <button
                onClick={fecharAnaliseIA}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  color: "white",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div
              style={{
                padding: "24px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {/* Loading */}
              {loadingIA && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                  }}
                >
                  <Bot
                    size={48}
                    style={{
                      marginBottom: "20px",
                      animation: "pulse 2s infinite",
                    }}
                  />
                  <p
                    style={{
                      fontSize: "16px",
                      color: theme.gray[600],
                      fontFamily: theme.fonts.sans,
                      marginBottom: "8px",
                    }}
                  >
                    Analisando o laudo...
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {exameExpandido?.tipo
                      ?.toLowerCase()
                      .includes("endoscopia") ||
                    exameExpandido?.tipo?.toLowerCase().includes("colonoscopia")
                      ? "Avaliando varizes, gastropatia e sinais de risco..."
                      : exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("ecocardiograma") ||
                        exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("cateterismo")
                      ? "Avaliando função cardíaca e elegibilidade para transplante..."
                      : exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("elastografia")
                      ? "Avaliando rigidez hepática e grau de fibrose..."
                      : exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("biópsia") ||
                        exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("anatomopatológico")
                      ? "Avaliando achados histopatológicos e grau tumoral..."
                      : exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("pulmonar") ||
                        exameExpandido?.tipo
                          ?.toLowerCase()
                          .includes("espirometria")
                      ? "Avaliando função pulmonar e risco cirúrgico..."
                      : "Avaliando lesões hepáticas, critérios de Milão e LI-RADS"}
                  </p>
                </div>
              )}

              {/* Erro */}
              {erroIA && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: theme.borderRadius.lg,
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      color: "#dc2626",
                      fontWeight: "600",
                      marginBottom: "8px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <X size={14} style={{ marginRight: "6px" }} /> Erro na
                    análise
                  </p>
                  <p
                    style={{
                      color: "#991b1b",
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {erroIA}
                  </p>
                </div>
              )}

              {/* Resultado */}
              {resultadoIA && !loadingIA && (
                // B301/AI_137: usa o renderizador compartilhado (trata #/##/###,
                // negrito, listas, ---, caixas de alerta por marcador real) e ESCONDE
                // o bloco técnico ACHADOS_EXAME. Antes havia um renderizador ad-hoc
                // aqui que mostrava o bloco cru e os "##" literais.
                <TextoAnaliseMarkdown texto={resultadoIA} />
              )}
            </div>

            {/* Footer do Modal */}
            {resultadoIA && !loadingIA && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: `1px solid ${theme.gray[200]}`,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  onClick={fecharAnaliseIA}
                  style={{
                    padding: "10px 24px",
                    background: theme.gray[100],
                    color: theme.gray[700],
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
